import { useState } from 'react';

import {
  ProviderAvatarBlock,
  ChakraFlex,
  ChakraFormControl,
  ChakraFormLabel,
  Close,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  IconButton,
} from '@equip.health/ui';
import {
  UNASSIGNED_OPTION,
  UNASSIGNED_PROVIDER,
} from '~/lib/constants/user.constants';

import { useGetProviderInfoWithHistoryByIdQuery } from '~/store/services/providers';
import { getLatestOutOfOfficeEndDate } from '~/lib/util/profile.util';
import ProviderUniversalSelect from '~/components/common/calendar/ProviderUniversalSelect';

const { label } = EquipUITheme.typography;
const { neutral } = EquipUIFireflyV2Theme.colors;

type AssigneeSelectionProps = {
  assignee: Partial<GetProviderByIdResponse> | undefined | null;
  hideLabel?: boolean;
  id: string;
  isNonEditable?: boolean;
  updateAssignee: (
    newAssignee: Partial<GetProviderByIdResponse> | undefined,
  ) => void;
};

const AssigneeSelection = ({
  assignee,
  hideLabel = false,
  id,
  isNonEditable = false,
  updateAssignee,
}: AssigneeSelectionProps) => {
  const TOP_LEVEL_ID = `${id}-assignee`;

  const { data: rawAssigneeData } = useGetProviderInfoWithHistoryByIdQuery(
    {
      includeAvatar: true,
      includeOutOfOffice: true,
      includeProviderHistory: false,
      providerExternalId: assignee?.userId,
    },
    { skip: !assignee?.userId },
  );
  const assigneeDetails = assignee?.userId ? rawAssigneeData : null;

  // TODO: best practice says we should use a reducer anytime a state variable gets even slightly
  // more complex than a simple primitive type

  const [showAutoComplete, setShowAutoComplete] = useState<boolean>(!assignee);

  const [selectedProviderValue, setSelectedProviderValue] =
    useState<ProviderUniversalSelectOption>(null);

  const handleAssigneeUniversalSelect = (
    selected: ProviderUniversalSelectOption,
  ): void => {
    setSelectedProviderValue(selected);
    let newAssignee: Partial<GetProviderByIdResponse> | undefined;
    const { userId } = selected;
    if (userId === UNASSIGNED_OPTION.value) {
      newAssignee = UNASSIGNED_PROVIDER;
    } else {
      newAssignee = selected;
    }
    setShowAutoComplete(false);
    updateAssignee(newAssignee);
  };

  /**
   * Clear the selection when "X" button is clicked
   */
  const onClearAssigneeClick = (): void => {
    updateAssignee(undefined);
    setShowAutoComplete(true);
    setSelectedProviderValue(null);
  };

  const oooEndDate = getLatestOutOfOfficeEndDate(assigneeDetails?.outOfOffice);

  const credentials = assigneeDetails?.credentialsDetails?.map(
    (credential) => credential.code,
  );

  const isDisplayName = assignee?.displayFirstName && assignee?.displayLastName;

  const assigneeAvatar =
    assigneeDetails?.avatar?.thumbnailUrl || assigneeDetails?.avatar?.url;

  const renderAssignedToLabel = () => (
    <ChakraFormControl isRequired>
      {!hideLabel && (
        <ChakraFormLabel
          {...label}
          color="neutral.secondary"
          marginInlineStart="unset"
          marginLeft="14px !important"
        >
          Assigned to
        </ChakraFormLabel>
      )}
    </ChakraFormControl>
  );

  if (showAutoComplete) {
    return (
      <>
        {renderAssignedToLabel()}
        <ProviderUniversalSelect
          handleSelect={handleAssigneeUniversalSelect}
          id={TOP_LEVEL_ID}
          selectedProvider={selectedProviderValue}
          shouldAddUnassignedOption
          shouldClearValueOnSelect
          shouldShowCurrentUserInDefaultList
        />
      </>
    );
  } else {
    return (
      <>
        {renderAssignedToLabel()}
        <ChakraFlex
          alignItems="center"
          background="neutral.background.primary"
          border={`1px solid ${neutral.line}`}
          borderRadius="16px"
          cursor="pointer"
          height="64px"
          justifyContent="space-between"
          marginTop="0px !important"
          onClick={() => {
            if (!isNonEditable) setShowAutoComplete(true);
            setSelectedProviderValue(null);
          }}
          paddingX="16px"
        >
          {assignee && (
            <ProviderAvatarBlock
              avatarUrl={assigneeAvatar}
              credentials={credentials}
              displayName={
                isDisplayName && {
                  firstName: assignee.displayFirstName,
                  lastName: assignee.displayLastName,
                }
              }
              id={TOP_LEVEL_ID}
              legalName={{
                firstName: assignee.firstName,
                lastName: assignee.lastName,
              }}
              oooDate={oooEndDate}
              providerTypeName={
                assignee?.providerTypeAtAssignment ||
                assignee?.providerTypeDetails?.name
              }
              shouldShowCredentials
              shouldShowOOOIcon={!!oooEndDate}
              shouldShowProviderType
              variant="standard"
            />
          )}
          {!isNonEditable && (
            <IconButton
              ariaLabel="clear assignee"
              icon={<Close fill={neutral.secondary} />}
              id={`${TOP_LEVEL_ID}__clear-assignee`}
              onClick={onClearAssigneeClick}
              size="sm"
              variant="ghost"
            />
          )}
        </ChakraFlex>
      </>
    );
  }
};

export default AssigneeSelection;
